// RC8 · Weekly Briefing read-time enrichment (images + links).
//
// Pure, dependency-free. Maps a briefing item's persisted provenance
// codes to (a) an in-app route and (b) a Media Manager hero subject.
// Nothing here fetches or fabricates: images are resolved later through
// the canonical hero endpoint (published heroes only) and links only
// target guides that exist in the locked knowledge index. Historical and
// restored editions are enriched without any document rewrite.

// `cabin.*` → Ship Guide · Decks (deck directory, PO 2026-09-24). Only
// cabin.assigned/guarantee link — packing/view tips stay unlinked.
const ROUTE_RULES = [
  [/^cabin\.(assigned|guarantee)$/, { route: "ship-guide:decks", linkLabel: "Open deck directory" }],
  [/^checkin\./, { route: "documents", linkLabel: "Open Documents" }],
  [/^(planning\.main_dining|dining\.)/, { route: "dining", linkLabel: "Open Dining" }],
  [/^(planning\.excursion|excursion)/, { route: "excursions", linkLabel: "Open Excursions" }],
  [/^finance\./, { route: "finances", linkLabel: "Open Finances" }],
];

// The engine stores `[source_kind:source_id, <signal code>]`; "What Changed"
// rows wrap the original code as `engine.new.<code>`.
export function itemCode(item) {
  const prov = Array.isArray(item?.provenance) ? item.provenance : [];
  const raw = prov.find((p) => typeof p === "string" && !p.includes(":")) || null;
  if (!raw) return null;
  return raw.startsWith("engine.new.") ? raw.slice("engine.new.".length) : raw;
}

export function portCkbId(portId) {
  if (!portId) return null;
  if (portId.startsWith("ckb-")) return portId;
  return `ckb-port-${portId.replace(/^port-/, "")}`;
}

// Prefer the guide named for the port itself (`ckb-destination-<slug>`),
// then a child guide whose id carries the port slug (e.g. San Miguel de
// Cozumel for Cozumel), otherwise the first locked child guide.
export function pickPortGuide(guides, portId) {
  const ckb = portCkbId(portId);
  if (!ckb || !Array.isArray(guides)) return null;
  const slug = ckb.replace(/^ckb-port-/, "");
  const children = guides.filter((g) => g && g.parent_port_id === ckb);
  return (
    children.find((g) => g.id === `ckb-destination-${slug}`)
    || children.find((g) => typeof g.id === "string" && g.id.includes(slug))
    || children[0]
    || null
  );
}

export function resolveItemEnrichment(item, { guides = [], seaDayGuides = [] } = {}) {
  const code = itemCode(item);
  if (!code) return null;

  const port = code.match(/^itinerary\.port\.(.+)$/);
  if (port) {
    const guide = pickPortGuide(guides, port[1]);
    return {
      route: guide ? `destination-guide:${guide.id}` : null,
      linkLabel: guide ? `Open ${guide.canonical_name} port guide` : null,
      media: { type: "port", id: port[1] },
    };
  }

  if (/^itinerary\.(front_sea_days|sea_days_total)$/.test(code)) {
    const first = Array.isArray(seaDayGuides)
      ? [...seaDayGuides].filter(Boolean).sort((a, b) => (a.day_number || 0) - (b.day_number || 0))[0]
      : null;
    if (!first) return null;
    return {
      route: `sea-day:${first.id}`,
      linkLabel: `Open ${first.canonical_name || "Sea Day Guide"}`,
      media: { type: "sea_day_guide", id: first.id },
    };
  }

  for (const [re, target] of ROUTE_RULES) {
    if (re.test(code)) return { ...target, media: null };
  }
  return null;
}
